import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import cn from 'classnames';

import InfiniteLooper from '../../InfiniteLooper';
import { divideArrayIntoSubArrays } from '../../../utils/arrays';
import { loadReactImageGallery } from '../../../utils/lazy-heavy-libs';

import styles from './styles.module.scss';
import Button from '../../shared/button';
import CloseIcon from '../../../icons/close-icon.inline.svg';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const AnimatedImageGallery = ({
  title = null,
  link = null,
  buttonLabel = 'get started for free',
  rows = 4,
  images,
  background = 'white',
}) => {
  const [imageUrls, setImageUrls] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const [openGallery, setOpenGallery] = useState(false);
  const [ImageGalleryComponent, setImageGalleryComponent] = useState(null);
  const wrapperRef = useRef(null);

  const genUrls = useCallback(() => {
    const arr = images?.map((image, idx) => {
      const source =
        document.getElementById(`img-${image.id}`)?.querySelector('img[data-main-image]') ||
        document.getElementById(`img-${image.id}`)?.querySelector('img') ||
        null;
      const urls = (source?.dataset?.srcset || source?.srcset)?.split(',') || source.src;
      return {
        id: image.id,
        listId: idx,
        thumbnail: urls?.[0]?.split(' ')?.[0]?.trim(),
        original: urls?.[urls.length - 1]?.trim()?.split(' ')?.[0]?.trim(),
      };
    });
    setImageUrls(arr);
  }, [images]);

  useIsomorphicLayoutEffect(() => {
    genUrls();
  }, [genUrls]);

  const imageRows = useMemo(() => divideArrayIntoSubArrays(imageUrls, rows), [imageUrls, rows]);

  const ensureGalleryLoaded = useCallback(() => {
    if (ImageGalleryComponent) return Promise.resolve(ImageGalleryComponent);
    return loadReactImageGallery().then((Comp) => {
      setImageGalleryComponent(() => Comp);
      return Comp;
    });
  }, [ImageGalleryComponent]);

  const handleOpenGallery = useCallback(
    (idx) => () => {
      ensureGalleryLoaded();
      setActiveImage(idx);
      setOpenGallery(true);
    },
    [ensureGalleryLoaded]
  );

  const handleCloseGallery = useCallback(() => {
    setOpenGallery(false);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!wrapperRef.current) return undefined;
    if (!('IntersectionObserver' in window)) return undefined;
    if (ImageGalleryComponent) return undefined;

    const el = wrapperRef.current;
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting || e.intersectionRatio > 0)) {
          observer.disconnect();
          ensureGalleryLoaded();
        }
      },
      { rootMargin: '600px 0px', threshold: 0.01 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [ensureGalleryLoaded, ImageGalleryComponent]);

  if (!imageUrls || !imageUrls.length) return null;

  return (
    <>
      <div className={cn(styles.gallery, styles[background])} ref={wrapperRef}>
        {title && (
          <div className={styles.infoModal}>
            <h4>{title}</h4>
            {link && (
              <Button to={link} theme="accent-primary" size="md">
                {buttonLabel}
              </Button>
            )}
          </div>
        )}
        {imageRows?.map((imgs, idx) => (
          <div className={styles.imagesRow} key={idx}>
            <InfiniteLooper speed={20 + idx * 2} direction="right">
              {imgs?.map(({ id, thumbnail, listId }) => (
                <button
                  key={id}
                  type="button"
                  onClick={handleOpenGallery(listId)}
                  onMouseEnter={ensureGalleryLoaded}
                  onFocus={ensureGalleryLoaded}
                >
                  <span className="is-sr-only">Open image gallery</span>
                  <img
                    src={thumbnail}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    width={220}
                    height={116}
                  />
                </button>
              ))}
            </InfiniteLooper>
          </div>
        ))}
      </div>
      {openGallery && (
        <div className={styles.galleryModal}>
          <button type="button" className={styles.closeGalleryBtn} onClick={handleCloseGallery}>
            <span className="is-sr-only">Close gallery</span>
            <CloseIcon />
          </button>
          {ImageGalleryComponent ? (
            <ImageGalleryComponent
              startIndex={activeImage}
              items={imageUrls}
              showBullets={false}
              showPlayButton={false}
              showFullscreenButton={false}
              thumbnailClass={styles.thumbnail}
              lazyLoad
            />
          ) : (
            <div style={{ minHeight: 240 }} />
          )}
        </div>
      )}
    </>
  );
};

AnimatedImageGallery.propTypes = {
  title: PropTypes.string,
  link: PropTypes.string,
  buttonLabel: PropTypes.string,
  rows: PropTypes.number,
  images: PropTypes.arrayOf(PropTypes.object).isRequired,
  background: PropTypes.oneOf(['white', 'darkblue']),
};

export default AnimatedImageGallery;
